import { useState, useCallback, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import TeacherShell, { TeacherPageHeader } from '@/components/layout/TeacherShell'
import { Card, CardContent } from '@/components/ui'
import GraphCanvas from '@/components/concepts/GraphCanvas'
import ConceptDetailPanel from '@/components/concepts/ConceptDetailPanel'
import ConceptLegend from '@/components/concepts/ConceptLegend'
import StudentSelector from '@/components/concepts/StudentSelector'

// ── Graph Tabs ──

function GraphTabs({ currentGraph, availableGraphs }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm mb-5">
      {availableGraphs.map(({ key, label, active }) => {
        const isCurrent = key === currentGraph

        if (!active) {
          return (
            <span
              key={key}
              className="px-4 py-1.5 text-gray-300 dark:text-gray-600 cursor-default select-none"
              title="Coming soon"
            >
              {label}
            </span>
          )
        }

        return (
          <Link
            key={key}
            href={`/teacher/concepts/${key}`}
            className={`px-4 py-1.5 transition-colors ${
              isCurrent
                ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-medium'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {label}
          </Link>
        )
      })}
    </div>
  )
}

// ── Stats Bar ──

function StatsBar({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      {[
        { label: 'Total Nodes', value: stats.total_nodes },
        { label: 'Prerequisite Edges', value: stats.total_edges },
        { label: 'Layers', value: Object.keys(stats.layer_distribution || {}).length },
      ].map(({ label, value }) => (
        <Card key={label}>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ── Main Page ──

export default function ConceptsIndex({ graph, available_graphs, concepts, edges, stats, students = [] }) {
  const [selectedNode, setSelectedNode] = useState(null)
  const [masteryData, setMasteryData] = useState(null)
  const [highlightedPath, setHighlightedPath] = useState(null)
  const [focusNodeId, setFocusNodeId] = useState(null)
  const [conceptFilter, setConceptFilter] = useState('all') // 'all' | 'sat' | 'foundational'

  const initialStudentId = useMemo(() => {
    const id = new URLSearchParams(window.location.search).get('student_id')
    return id ? parseInt(id, 10) : null
  }, [])

  const graphLabel = available_graphs.find(g => g.key === graph)?.label || graph

  const satCount = useMemo(() => concepts.filter(c => c.sat_tested !== false).length, [concepts])
  const foundationalCount = useMemo(() => concepts.filter(c => c.sat_tested === false).length, [concepts])

  const filteredConcepts = useMemo(() => {
    if (conceptFilter === 'sat') return concepts.filter(c => c.sat_tested !== false)
    if (conceptFilter === 'foundational') return concepts.filter(c => c.sat_tested === false)
    return concepts
  }, [concepts, conceptFilter])

  const filteredEdges = useMemo(() => {
    const nodeIds = new Set(filteredConcepts.map(c => c.sat_node_id || c.id))
    return edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target))
  }, [edges, filteredConcepts])

  const handleSelectNode = useCallback((node) => {
    setSelectedNode(prev => {
      if (!node) return null
      const prevId = prev?.sat_node_id || prev?.id
      const newId = node?.sat_node_id || node?.id
      return prevId === newId ? null : node
    })
    setHighlightedPath(null)
  }, [])

  const handleSelectConceptById = useCallback((id) => {
    const found = filteredConcepts.find(c => (c.sat_node_id || c.id) === id || c.id === id)
    if (found) {
      setSelectedNode(found)
      setFocusNodeId(id)
    }
  }, [filteredConcepts])

  const handleFocusNode = useCallback((id) => {
    setFocusNodeId(id)
  }, [])

  const handleTraceWeakness = useCallback(async (conceptId) => {
    const visited = new Set()
    const pathEdges = []
    const queue = [conceptId]

    while (queue.length > 0) {
      const current = queue.shift()
      if (visited.has(current)) continue
      visited.add(current)

      const prereqs = filteredEdges.filter(e => e.source === current)
      for (const edge of prereqs) {
        pathEdges.push(edge)
        const mastery = masteryData?.[edge.target]
        if (!mastery || mastery.mastery_level < 0.5) {
          queue.push(edge.target)
        }
      }
    }

    setHighlightedPath({ edges: pathEdges, rootNode: conceptId })
  }, [filteredEdges, masteryData])

  const handleMasteryLoaded = useCallback((data) => {
    setMasteryData(data)
    setHighlightedPath(null)
  }, [])

  return (
    <TeacherShell scrollTitle={<>Concepts &rsaquo; {graphLabel}</>}>
      <TeacherPageHeader
        title="Concepts"
        subtitle={`${stats.total_nodes} ${graphLabel.toLowerCase()} concepts \u00B7 ${stats.total_edges} prerequisite edges`}
      />

      <GraphTabs currentGraph={graph} availableGraphs={available_graphs} />

      {foundationalCount > 0 && (
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm mb-5 ml-3">
          {[
            { key: 'all', label: `All (${concepts.length})` },
            { key: 'sat', label: `SAT (${satCount})` },
            { key: 'foundational', label: `Foundational (${foundationalCount})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setConceptFilter(key); setFocusNodeId(null) }}
              className={`px-3 py-1.5 transition-colors ${
                conceptFilter === key
                  ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-medium'
                  : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <StatsBar stats={stats} />

      {/* Main content: graph + sidebar */}
      <div className="flex gap-4">
        {/* Graph canvas */}
        <div className="flex-1 min-w-0">
          <GraphCanvas
            concepts={filteredConcepts}
            edges={filteredEdges}
            masteryData={masteryData}
            selectedNode={selectedNode}
            highlightedPath={highlightedPath}
            onSelectNode={handleSelectNode}
            focusNodeId={focusNodeId}
            onFocusNode={handleFocusNode}
          />
        </div>

        {/* Right sidebar */}
        <div className="w-64 shrink-0 space-y-3">
          <StudentSelector graph={graph} students={students} initialStudentId={initialStudentId} onMasteryLoaded={handleMasteryLoaded} />
          <ConceptLegend hasMasteryData={!!masteryData} graph={graph} />

          {selectedNode && (
            <ConceptDetailPanel
              concept={selectedNode}
              concepts={filteredConcepts}
              edges={filteredEdges}
              masteryData={masteryData}
              graph={graph}
              onClose={() => { setSelectedNode(null); setHighlightedPath(null) }}
              onTraceWeakness={handleTraceWeakness}
              onSelectConcept={handleSelectConceptById}
            />
          )}
        </div>
      </div>
    </TeacherShell>
  )
}
